<script lang="ts">
  /**
   * The sign-in view (R4.9 §7, Task 8): one `[ Sign in with Google ]` action and,
   * on a relay that already has an account, the optional invite field (§6).
   *
   * The action is a plain ANCHOR, not a fetch and not a form submit. The relay
   * answers `/api/auth/signin` with a 302 to Google, so the whole flow is a
   * full-page navigation that ends back at `/` with the login cookie set: the
   * browser leaves the app and boots again through the gate, which reads the
   * cookie. There is no in-place login state to reconcile and nothing here to
   * await.
   *
   * The invite rides the query string because that is the surface the relay
   * specifies; the server keeps it beside the nonce, out of the consent URL and
   * out of the cookie (R12). The field is shown ONLY in account mode: the first
   * signup CLAIMS the relay and needs no code, so a field there would ask a
   * question with no answer (§6).
   *
   * Whether this view may be shown at all is the host's decision (D2: never when
   * Google is unconfigured); `signin_available` is not re-checked here.
   */
  import CircleUser from 'lucide/dist/esm/icons/circle-user.mjs';
  import { signInUrl, type AuthState } from '../storage/account';
  import Icon from './Icon.svelte';

  interface Props {
    /** The gate's verdict: the mode decides whether an invite exists to ask for. */
    auth: AuthState;
    /**
     * Leaves the view. Absent when the gate put it up — an account-mode relay
     * with no live login has nowhere to go back to.
     */
    onBack?: () => void;
  }

  let { auth, onBack }: Props = $props();

  let invite = $state('');

  /** The action's href, re-derived as the code is typed. */
  const href = $derived(signInUrl(invite));
</script>

<div class="signin-view" data-testid="signin-view">
  <h1>Sign in</h1>

  <p class="signin-copy" data-testid="signin-copy">
    {#if auth.mode === 'account'}
      Sign in to reach your notebooks.
    {:else}
      The first person to sign in becomes the admin, and inherits the notebooks
      already here.
    {/if}
  </p>

  {#if auth.mode === 'account'}
    <label class="signin-invite">
      <span>Have an invite code?</span>
      <input
        type="text"
        autocomplete="off"
        spellcheck="false"
        data-testid="signin-invite"
        bind:value={invite}
      />
    </label>
  {/if}

  <a class="action signin-google" data-testid="signin-google" href={href}>
    <Icon node={CircleUser} size={14} />
    <span>Sign in with Google</span>
  </a>

  {#if onBack}
    <button class="action" type="button" data-testid="signin-back" onclick={onBack}>
      [ Back ]
    </button>
  {/if}
</div>

<style>
  .signin-view {
    width: min(100%, 60em);
    margin: 0 auto;
    padding: 2rem 1rem;
  }

  h1 {
    font-family: inherit;
    font-size: 1.4rem;
    font-weight: normal;
  }

  .signin-copy {
    max-width: 34em;
    color: var(--ink-muted);
  }

  .signin-invite {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    margin: 0 0 1rem;
  }

  .signin-invite input {
    font-family: var(--font-mono, monospace);
    font-size: 12px;
    padding: 2px 6px;
    border: 1px solid color-mix(in srgb, var(--ink) 50%, transparent);
    background: transparent;
    color: var(--ink);
  }

  .signin-google {
    margin-right: 0.75rem;
  }
</style>
